import type { IncidentEntity, ReportEntity } from '@data/utils/types';

import { priceIncidentReport } from '../../products/productDetail/incidentCosts';
import { PRIORITIES, type Priority, type SoldSupport } from '../../products/productDetail/serviceLevels';

export const NO_PRIORITY = 'none' as const;
export type IncidentBucket = Priority | typeof NO_PRIORITY;

/** The grid's row id for one product's incidents at one priority. */
export const incidentRowId = (productId: string, bucket: IncidentBucket): string => `${productId}:${bucket}`;

export interface IncidentSprintFigures {
  /** Hours by sprint id, then by row id. */
  hours: Record<string, Record<string, number>>;
  /** Euros the same way; a row left out where someone on it has no rate. */
  costs: Record<string, Record<string, number>>;
  /** Each product's rows: P1 to P4 always, then time on incidents with no priority, if any. */
  bucketsByProduct: Record<string, IncidentBucket[]>;
}

/** Sums each product's incident reports into sprint and priority cells, priced as the product's Spend tab prices them. */
export const incidentFiguresBySprint = (
  products: {
    id: string;
    rateAt: (day: string, memberId: string) => number | null;
    serviceAt: (day: string) => SoldSupport | null;
    incidents: IncidentEntity[];
    reportsBySprint: Record<string, ReportEntity[]>;
  }[],
): IncidentSprintFigures => {
  const hours: IncidentSprintFigures['hours'] = {};
  const costs: IncidentSprintFigures['costs'] = {};
  const unpriced = new Set<string>();
  const bucketsByProduct: IncidentSprintFigures['bucketsByProduct'] = {};

  products.forEach(product => {
    const byId = new Map(product.incidents.map(incident => [incident.id, incident]));
    const used = new Set<IncidentBucket>();

    Object.entries(product.reportsBySprint).forEach(([sprintId, reports]) => {
      reports.forEach(report => {
        if (!report.incidentId) return;
        const incident = byId.get(report.incidentId);
        const bucket: IncidentBucket = incident?.priority ?? NO_PRIORITY;
        const row = incidentRowId(product.id, bucket);
        const charge = priceIncidentReport(report, incident, product.rateAt, product.serviceAt);
        used.add(bucket);
        hours[sprintId] = { ...hours[sprintId], [row]: (hours[sprintId]?.[row] ?? 0) + charge.hours };
        if (charge.cost === null) unpriced.add(`${sprintId}|${row}`);
        costs[sprintId] = { ...costs[sprintId], [row]: (costs[sprintId]?.[row] ?? 0) + (charge.cost ?? 0) };
      });
    });
    bucketsByProduct[product.id] = [...PRIORITIES, ...(used.has(NO_PRIORITY) ? [NO_PRIORITY] : [])];
  });

  // To the cent and the hundredth of an hour, and blank rather than a part-priced figure.
  const cents = (value: number): number => Math.round(value * 100) / 100;
  const rounded = (table: IncidentSprintFigures['hours'], skip: (key: string) => boolean) =>
    Object.fromEntries(
      Object.entries(table).map(([sprintId, rows]) => [
        sprintId,
        Object.fromEntries(
          Object.entries(rows)
            .filter(([row]) => !skip(`${sprintId}|${row}`))
            .map(([row, value]) => [row, cents(value)]),
        ),
      ]),
    );
  return {
    hours: rounded(hours, () => false),
    costs: rounded(costs, key => unpriced.has(key)),
    bucketsByProduct,
  };
};
